import React from 'react';
import Icon from '@mui/material/Icon';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import { FieldCard } from '../ui/FieldCard';

function onlyDigits(s) {
  return String(s || '').replace(/\D+/g, '');
}

function isValidTurkishPhone(phone) {
  return /^05\d{9}$/.test(onlyDigits(phone));
}

function formatTurkishPhone(value) {
  const d = onlyDigits(value).slice(0, 11);

  if (d.length <= 4) return d;
  if (d.length <= 7) return `${d.slice(0, 4)} ${d.slice(4)}`;
  if (d.length <= 9) return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7)}`;
  return `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7, 9)} ${d.slice(9, 11)}`;
}

function getPhoneState(phone) {
  const digits = onlyDigits(phone);

  if (!digits) {
    return { tone: 'neutral', text: '' };
  }

  if (digits.length < 11) {
    return {
      tone: 'info',
      text: 'Telefon numarasını 11 hane olarak girebilirsin.',
    };
  }

  if (!isValidTurkishPhone(digits)) {
    return {
      tone: 'warning',
      text: 'Numarayı 05xx xxx xx xx formatında kontrol edelim.',
    };
  }

  return {
    tone: 'success',
    text: 'Numara hazır.',
  };
}

export function MemberInfoCard({ form, set }) {
  const theme = useMuiTheme();
  const phoneState = getPhoneState(form.phone);

  const handlePhoneChange = (e) => {
    const digits = onlyDigits(e.target.value).slice(0, 11);
    set('phone', digits);
  };

  return (
    <FieldCard icon="badge" tone="teal" title="Üye Bilgileri" subtitle="Ad, soyad, yaş ve telefon">
      <MDBox sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
        <MDInput
          label="Ad"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          error={!form.name || form.name.trim().length === 0}
        />

        <MDInput
          label="Soyad"
          value={form.surname}
          onChange={(e) => set('surname', e.target.value)}
          error={!form.surname || form.surname.trim().length === 0}
        />
      </MDBox>

      <MDBox
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr' }, gap: 1.25 }}
      >
        <MDInput
          label="Doğum Tarihi"
          type="date"
          value={form.birthDate || ''}
          onChange={(e) => set('birthDate', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <MDBox>
          <MDInput
            label="Telefon"
            value={formatTurkishPhone(form.phone)}
            inputProps={{ inputMode: 'tel', maxLength: 14 }}
            onChange={handlePhoneChange}
            placeholder="05xx xxx xx xx"
          />

          {!!phoneState.text && (
            <MDBox
              sx={{
                mt: 0.75,
                px: 1.1,
                py: 0.9,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                border: `1px solid ${
                  phoneState.tone === 'warning'
                    ? 'rgba(255,152,0,.24)'
                    : phoneState.tone === 'success'
                    ? 'rgba(76,175,80,.24)'
                    : 'rgba(33,150,243,.18)'
                }`,
                background:
                  phoneState.tone === 'warning'
                    ? 'rgba(255,152,0,.08)'
                    : phoneState.tone === 'success'
                    ? 'rgba(76,175,80,.08)'
                    : 'rgba(33,150,243,.06)',
              }}
            >
              <Icon sx={{ fontSize: 16, opacity: 0.9 }}>
                {phoneState.tone === 'warning'
                  ? 'warning_amber'
                  : phoneState.tone === 'success'
                  ? 'check_circle'
                  : 'info'}
              </Icon>

              <MDTypography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
              >
                {phoneState.text}
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <MDBox
        sx={{
          mt: 0.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.1,
          borderRadius: '14px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.action.hover,
        }}
      >
        <Icon sx={{ fontSize: 18, opacity: 0.85 }}>info</Icon>
        <MDTypography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          Zorunlu alanlar: Ad + Soyad. Diğerlerini sonra da ekleyebilirsin.
        </MDTypography>
      </MDBox>
    </FieldCard>
  );
}
